/**
 * The geometry and timing of a touch, as pure functions.
 *
 * Pure because this is the logic the Zielübung (1.12) mines to infer whether this person has a
 * field loss, an acuity limitation or a systematic motor bias — and an inference built on
 * untested arithmetic is worse than no inference, because a caregiver will act on it.
 *
 * Three protections, all from §5.1's DirectTouch row:
 *
 *  - **activate on release within target**, so a finger that lands wrong can be dragged to the
 *    right key before lifting. For impaired motor control this is the difference between a typo
 *    and a correction.
 *  - **slop tolerance**, so leaving the target by a millimetre during the press is forgiven.
 *  - **repeat suppression**, because tremor produces double-taps that mean one keystroke.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Apply the calibration offset. A person may aim consistently low-left and be unaware of it. */
export function correctPoint(point: Point, correction: Point): Point {
  return { x: point.x + correction.x, y: point.y + correction.y };
}

export function centreOf(rect: Rect): Point {
  return { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
}

/** Signed offset from the target centre — the vector calibration reads for directional bias. */
export function offsetFromCentre(point: Point, rect: Rect): Point {
  const centre = centreOf(rect);
  return { x: point.x - centre.x, y: point.y - centre.y };
}

/** Inside the target, grown by `slopPx` on every side. */
export function isWithin(point: Point, rect: Rect, slopPx: number): boolean {
  return (
    point.x >= rect.left - slopPx &&
    point.x <= rect.right + slopPx &&
    point.y >= rect.top - slopPx &&
    point.y <= rect.bottom + slopPx
  );
}

/**
 * Should this activation be suppressed as a tremor repeat?
 *
 * Per target, not global: pressing `A` then `S` quickly is deliberate typing, while `A` then `A`
 * inside the window is almost certainly one intended press. Suppressing globally would throttle
 * someone typing well.
 */
export function isRepeat(args: {
  targetId: string;
  atMs: number;
  lastTargetId: string | null;
  lastAtMs: number | null;
  repeatSuppressMs: number;
}): boolean {
  const { targetId, atMs, lastTargetId, lastAtMs, repeatSuppressMs } = args;
  if (lastTargetId !== targetId || lastAtMs === null) return false;
  return atMs - lastAtMs < repeatSuppressMs;
}

export type TouchOutcome =
  | { kind: 'activate'; targetId: string; offset: Point }
  | { kind: 'suppressed'; targetId: string }
  | { kind: 'missed'; offset: Point }
  | { kind: 'ignored' };

/**
 * Resolve a completed press into an outcome.
 *
 * `missed` is as important as `activate` and is reported even though nothing happens on screen:
 * it is the raw material for the position heatmap, and a device that only records successes cannot
 * tell us that everything on the left is being missed.
 */
export function resolveTouch(args: {
  releasePoint: Point;
  pressTargetId: string | null;
  releaseTargetId: string | null;
  targetRect: Rect | null;
  slopPx: number;
  atMs: number;
  lastTargetId: string | null;
  lastAtMs: number | null;
  repeatSuppressMs: number;
}): TouchOutcome {
  const { releasePoint, pressTargetId, releaseTargetId, targetRect, slopPx } = args;

  if (!pressTargetId || !targetRect) {
    // The press began on nothing interactive. Not a miss against a target — we have no target to
    // measure an offset against — so it is not counted against the person.
    return { kind: 'ignored' };
  }

  const offset = offsetFromCentre(releasePoint, targetRect);

  // Slid off the key before lifting: deliberately no activation. Recorded as a miss because a
  // consistent direction of sliding is itself a finding.
  if (releaseTargetId !== pressTargetId && !isWithin(releasePoint, targetRect, slopPx)) {
    return { kind: 'missed', offset };
  }

  if (
    isRepeat({
      targetId: pressTargetId,
      atMs: args.atMs,
      lastTargetId: args.lastTargetId,
      lastAtMs: args.lastAtMs,
      repeatSuppressMs: args.repeatSuppressMs,
    })
  ) {
    return { kind: 'suppressed', targetId: pressTargetId };
  }

  return { kind: 'activate', targetId: pressTargetId, offset };
}
